/*
* This document contains all of the actions related to fetching, listening,
* creating, updating, deleting a user's data. If a user is not is not authed
* these actions will fail.
*/
import firebase from 'firebase';
import 'firebase/firestore';
import 'firebase/functions';
import { EventTypes } from 'redux-segment';
import _ from 'lodash';
import Log from 'lib/Log';
import { makeDebugger } from 'lib';

import * as at from './actionTypes';
import {
  TAGS,
  ENTRIES,
  USERS,
  DATA_ADDED,
  DATA_REMOVED,
  DATA_UPDATED
} from './firebaseNaming';

const debug = makeDebugger('data/actions');

/*
 * Retrieves user doc reference for currently signed in user
 * TODO: handle error when user not signed in...
 */
const userRef = () => {
  const { currentUser } = firebase.auth();
  return currentUser ?
      firebase.firestore().collection(USERS).doc(currentUser.uid) : null;
}

export const unlisten = () => {
  return { type: at.UNLISTEN };
}

/**
 * retrieve and watches changes on all entries
 */
export const entriesWatch = () => {
  return dispatch => {
    dispatch({type: at.ENTRIES_FETCH});
    const entriesCollection = userRef().collection(ENTRIES);
    const unsubscribe = entriesCollection
      .onSnapshot((querySnapshot) => {
        const entries = { added: [], updated: [], removed: [] };
        for (let docChange of querySnapshot.docChanges()) {
          // TODO: decouple naming scheme in DB, with use in app?
          const tags = _.keys(docChange.doc.data().tags);
          const id = docChange.doc.id;
          const entry = { id, ...docChange.doc.data(), tags };
          // handle all new entries
          switch(docChange.type) {
            case DATA_ADDED:
              entries.added.push(entry);
              break;
            case DATA_UPDATED:
              entries.updated.push(entry);
              // TODO: only do this is tags are changed??
              dispatch({type: at.TAGS_UPDATE_ON_ENTRY, payload: {id, tags }});
              break;
            case DATA_REMOVED:
              entries.removed.push(docChange.doc.id);
              break;
            default:
              Log.error("Didn't recognize data change type...", 'data/actions:entriesWatch')
          }
        }
        if (entries.added.length > 0)
          dispatch({type: at.ENTRIES_ADDED, payload: entries.added});
        if (entries.updated.length > 0)
          dispatch({type: at.ENTRIES_UPDATED, payload: entries.updated});
        if (entries.removed.length > 0) {
          debug("Entry Deleted");
          dispatch({type: at.ENTRIES_DELETED, payload: entries.removed});
        }
        // OVERALL DONE FETCHING ACTION
        dispatch({type: at.ENTRIES_FETCH_SUCCESS, payload: entries});
      }, (err) => {
        Log.error(err.message, 'data/actions:entriesWatch');
      });
    dispatch({type: at.LISTENER_ADD, payload: unsubscribe});
  }
}

const getTagId = (tagName) => {
  return _.replace(tagName, /\//g, "\\~");
}

const stageTagsAddToEntry = (tags, entryId, batch) => {
  const db = userRef();
  const entryDoc = db.collection(ENTRIES).doc(entryId.toString());
  const newTags = {};
  for (let tagName of tags) {
    debug("Adding a Tag to Entry: ", tagName);
    newTags[tagName] = new Date();
    let tagId = getTagId(tagName);
    let tagDoc = db.collection(TAGS).doc(tagId);
    let tag = { name: tagName, ref: tagDoc, entries: {}, mostRecentDateAdded: new Date() };
    // Create tag, if tag doesn't exist - but merge if there is existing data
    batch.set(tagDoc, tag, {merge: true});
    const newEntry = {}
    newEntry[entryId] = true;
    batch.set(tagDoc, { entries: newEntry }, { merge: true })
  }
  batch.set(entryDoc, { tags: newTags }, { merge: true })
  return batch;
}

const createTagsForEntry = (tags) => {
  if (!tags) return {};
  return _.reduce(tags, (tagsObj, tag) => {
    tagsObj[tag] = true;
    return tagsObj;
  },{})
}

const batchAddEntries = (entries, dispatch, method) => {
  let batch = firebase.firestore().batch();
  let entryIds = [];
  for (let entry of entries) {
    const entryRef = userRef().collection(ENTRIES).doc()
    entryIds.push(entryRef.id);
    batch.set(entryRef, {
      url: entry.url,
      title: entry.title,
      faviconUrl: entry.faviconUrl || '',
      isRead: false,
      dateAdded: new Date(),
      tags: createTagsForEntry(entry.tags)
    });
    if (entry.tags && entry.tags.length > 0) {
      batch = stageTagsAddToEntry(entry.tags, entryRef.id, batch);
    }
  }
  //add tabs to firebase
  batch.commit().then(() => {
    dispatch({
      type: at.ENTRY_ADD_SUCCESS,
      meta: {
        analytics: {
          eventType: EventTypes.track,
          eventPayload: {
            event: 'Entry Created',
            properties: {
              type: 'url',
              number_entries: entries.length,
              entry_ids: entryIds,
              entry_urls: _.map(entries, (e) => e.url),
              method: method,
              has_tags: entries[0].tags && entries[0].tags.length > 0
            }
          }
        }
      }
    })
  }).catch((err) => {
    dispatch({ type: at.ENTRY_ADD_FAILURE, payload: err })
    Log.error('data/actions:batchAddEntries', err.message);
  })
}

export const addUrls = (urls) => {
  return dispatch => {
    if (!urls || urls.length === 0) return;
    dispatch({ type: at.ENTRY_ADD });
    const getInfo = firebase.functions().httpsCallable("getInfoUrls");
    getInfo({ urls })
      .then(result => {
        batchAddEntries(result.data.entries, dispatch, 'add urls modal');
      })
      .catch(err => {
        console.log(err)
        Log.error('data/actions:addUrls', err.message);
        dispatch({ type: at.ENTRY_ADD_FAILURE, payload: err })
      })
  }
}

export const addEntries = (entries) => {
  return dispatch => {
    if (!entries || entries.length === 0) return;
    dispatch({ type: at.ENTRY_ADD });
    batchAddEntries(entries, dispatch, 'extension');
  }
}

const stageTagsRemoveFromEntry = (tags, entryId, batch) => {
  const db = userRef();
  const entryDoc = db.collection(ENTRIES).doc(entryId.toString());
  const tagUpdate = [];
  for (let tagName of tags) {
    // TODO: Fix so it creates an array of objects to pass at the end
    let fieldPath = new firebase.firestore.FieldPath(`tags`,`${tagName}`)
    tagUpdate.push(fieldPath, firebase.firestore.FieldValue.delete())
    // batch.update(entryDoc, fieldPath, firebase.firestore.FieldValue.delete())
    // tagUpdate[`tags.${tagName}`] = firebase.firestore.FieldValue.delete();
    let tagId = getTagId(tagName);
    let tagDoc = db.collection(TAGS).doc(tagId);
    let entryUpdate = {};
    entryUpdate[`entries.${entryId}`] = firebase.firestore.FieldValue.delete();
    batch.update(tagDoc, entryUpdate);
  }
  batch.update(entryDoc, ...tagUpdate);
  return batch;
}

// export const helperGetTagsInCommon = (entries) => {
//   if (entries.length === 0) return [];
//   const firstEntry = entries[0];
//   if (!firstEntry.tags || firstEntry.tags.length === 0) return [];
//   let tagsInCommon = [...firstEntry.tags];
//   for (let entry of entries) {
//     if (!entry.tags || entry.tags.length === 0) return [];
//     tagsInCommon = _.intersection(tagsInCommon, entry.tags)
//   }
//   return tagsInCommon;
// }

const getAnalyticsObj = (entries, tagsToAdd, tagsToRemove) => {
  const analytics = [];
  if (tagsToAdd && tagsToAdd.length > 0) {
    analytics.push({
      eventType: EventTypes.track,
      eventPayload: {
        event: 'Entry Tagged',
        properties: {
          number_entries: entries.length,
          method: 'edit panel',
          entry_ids: entries,
          tags: tagsToAdd
        }
      }
    })
  }
  if (tagsToRemove && tagsToRemove.length > 0) {
    analytics.push({
      eventType: EventTypes.track,
      eventPayload: {
        event: 'Entry Untagged',
        properties: {
          number_entries: entries.length,
          method: 'edit panel',
          entry_ids: entries,
          tags: tagsToRemove
        }
      }
    })
  }
  return analytics;
}

/**
 * A batch update, changes the tags on the specified entries
 * To contain the ones passed in
 */
export const tagsUpdateOnEntries = (entries, tagsToAdd, tagsToRemove) => {
  return dispatch => {
    let batch = firebase.firestore().batch();
    for (let entryId of entries) {
      if (tagsToAdd && tagsToAdd.length > 0)
        batch = stageTagsAddToEntry(tagsToAdd, entryId, batch);
      if (tagsToRemove && tagsToRemove.length > 0)
        batch = stageTagsRemoveFromEntry(tagsToRemove, entryId, batch);
    }

    batch.commit().then(() => {
      dispatch({
        type: at.TAGS_UPDATE_ON_ENTRY_SUCCESS,
        meta: { analytics: getAnalyticsObj(entries, tagsToAdd, tagsToRemove) }
      })
    }).catch((err) => {
      Log.error(err.message, 'data/actions:UpdatingTagsOnEntry');
      dispatch({ type: at.TAGS_UPDATE_ON_ENTRY_FAILURE })
    });
  }
}

// Adds an array of tags to an entry in the DB
export const tagsAddToEntry = (tags, entryId) => {
  debug("Adding tags to entry: ", entryId)
  return dispatch => {
    dispatch({ type: at.TAGS_ADD_TO_ENTRY })
    let batch = firebase.firestore().batch();
    batch = stageTagsAddToEntry(tags, entryId, batch);
    batch.commit().then(() => {
      dispatch({ type: at.TAGS_ADD_TO_ENTRY_SUCCESS })
    }).catch((err) => {
      Log.error(err.message, 'data/actions:AddingTagsToEntry');
      dispatch({ type: at.TAGS_ADD_TO_ENTRY_FAILURE })
    });
  }
}

// Adds an array of tags to all entries in the passed array
export const batchTagsAddToEntries = (tags, entries) => {
  return dispatch => {
    dispatch({ type: at.TAGS_ADD_TO_ENTRY })
    let batch = firebase.firestore().batch();
    for (let entryId of entries) {
      batch = stageTagsAddToEntry(tags, entryId, batch)
    }
    batch.commit().then(() => {
      dispatch({ type: at.TAGS_ADD_TO_ENTRY_SUCCESS })
    }).catch((err) => {
      Log.error(err.message, 'data/actions:batchTagsAddToEntries');
      dispatch({ type: at.TAGS_ADD_TO_ENTRY_FAILURE })
    });
  }
}

// TODO: TEST TEST TEST Removes one Tag from an array of entries
export const tagDeleteFromEntries = (tag, entries) => {
  return dispatch => {
    let batch = firebase.firestore().batch();
    for (let entryId of entries) {
      batch = stageTagsRemoveFromEntry([ tag ], entryId, batch);
    }
    batch.commit().then(() => {
      debug("success saving tags to entry");
    }).catch((err) => {
      Log.error(err.message, 'data/actions:tagDeleteFromEntries');
    });
  }
}

/*---------------------------------- */

const stageEntryDelete = (entry, batch) => {
  let id = entry.id;
  let tags = entry.tags;
  let entryDoc = userRef().collection(ENTRIES).doc(id);
  // Get all tagDocs for entry
  for (let t of tags) {
    // for each tag Doc, remove entry
    let tagDoc = userRef().collection(TAGS).doc(getTagId(t));
    let entryUpdate = {};
    entryUpdate[`entries.${id}`] = firebase.firestore.FieldValue.delete();
    batch.update(tagDoc, entryUpdate);
  }
  batch.delete(entryDoc);
  return batch;
}

/*
 * Removing entries
 * TODO: should I not be using collections for tags on an entry?? Probably not... CHANGE
 */
export const entriesDelete = (entries) => {
  return dispatch => {
    dispatch({type: at.ENTRY_REMOVE})
    let batch = firebase.firestore().batch();
    for (let entry of entries) {
      batch = stageEntryDelete(entry, batch);
    }
    batch.commit().then(() => {
      debug("success removing entries");
      dispatch({type: at.ENTRY_REMOVE_SUCCESS})
    }).catch((err) => {
      Log.error(err.message, 'data/actions:entriesDelete');
      dispatch({type: at.ENTRY_REMOVE_FAILURE})
    });
  }
}

export const entryDelete = (entry) => {
  return dispatch => {
    dispatch({type: at.ENTRY_REMOVE})
    let batch = firebase.firestore().batch();
    batch = stageEntryDelete(entry, batch);
    batch.commit().then(() => {
      debug("success removing entry");
      dispatch({type: at.ENTRY_REMOVE_SUCCESS})
    }).catch((err) => {
      Log.error(err.message, 'data/actions:entryDelete');
      dispatch({type: at.ENTRY_REMOVE_FAILURE})
    });
  }
}

/**
 * @param  {array} updatedEntries An array of objects..
 *    {id: entryId, data:{changedValueKey: changedValue, etc... }}
 */
export const entriesUpdate = (updatedEntries) => {
  return dispatch => {
    dispatch({type: at.ENTRY_UPDATE})
    const batch = firebase.firestore().batch();
    for (let entry of updatedEntries) {
      let id = entry.id;
      let entryDoc = userRef().collection(ENTRIES).doc(id);
      debug("Updating Entry", entry.data);
      batch.set(entryDoc, entry.data, {merge: true});
    }
    batch.commit().then(() => {
      debug("success updating entries");
      dispatch({type: at.ENTRY_UPDATE_SUCCESS})
    }).catch((err) => {
      Log.error(err.message, 'data/actions:entriesUpdate');
      dispatch({type: at.ENTRY_UPDATE_FAILURE})
    });
  }
}


export const tagDelete = (tagName) => {
  return dispatch => {
    dispatch({type: at.TAG_DELETE, payload: tagName});
    let tagDoc = userRef().collection(TAGS).doc(getTagId(tagName));
    let deleteValue = firebase.firestore.FieldValue.delete();
    firebase.firestore().runTransaction((transaction) => {
      return transaction.get(tagDoc).then((tag) => {
        if (tag.data() && tag.data() !== undefined) {
          const entries = tag.data().entries;
          let fieldPath = new firebase.firestore.FieldPath(`tags`,`${tagName}`);
          for (let id in entries) {
            const entryDoc = userRef().collection(ENTRIES).doc(id);
            transaction.update(entryDoc, fieldPath, deleteValue);
          }
        }
        transaction.delete(tagDoc);
      })
    })
    .then(() => {
      debug("Tag Delete Success");
      dispatch({type: at.TAG_DELETE_SUCCESS})
    }).catch((err) => {
      Log.error(err.message, 'data/actions:tagDelete');
      dispatch({type: at.TAG_DELETE_FAILURE})
    })
  }
}

// retrieve all tags
export const tagsWatch = () => {
  return dispatch => {
    const unsubscribe = userRef().collection(TAGS)
      .onSnapshot((querySnapshot) => {
        var tags = [];
        var tagDateMap = {};
        querySnapshot.forEach((tag) => {
          tags.push(tag.data().name);
          tagDateMap[tag.data().name] = tag.data().mostRecentDateAdded || null;
        })
        dispatch({ type: at.TAGS_FETCH_SUCCESS, payload: {tags, tagDateMap} })
      }, (err) => {
        Log.error(err.message, 'data/actions:tagsWatch');
      });
    dispatch({type: at.LISTENER_ADD, payload: unsubscribe});
  }
}



// WEBPACK FOOTER //
// ./src/data/actions.js